// The bid grid's row rules (Lane 1331, Phase 2 slice 2B). Pure functions, no
// React and no data loading, so the rules are in one place.
//
// A row on screen holds text as typed: { price, lead, moq, no_bid, note }.
// The server checks every rule again (helpers/sourcing_offers.py DraftPartIn);
// these checks only stop a bad row from being sent and say what to fix.

export const EMPTY_ROW = Object.freeze({ price: '', lead: '', moq: '', no_bid: false, note: '' });

export const PRICE_MAX = 1000000000;
export const LEAD_MAX = 3650;
export const MOQ_MAX = 1000000000;
export const NOTE_MAX = 1000;
export const EVENT_NOTE_MAX = 4000;
export const SAVE_BATCH = 500;

// "12.5000" from the server reads as "12.5"; "10.0000" reads as "10".
export function trimPrice(value) {
  if (value === null || value === undefined || value === '') return '';
  const text = String(value);
  if (!text.includes('.')) return text;
  return text.replace(/0+$/, '').replace(/\.$/, '');
}

const asText = (v) => (v === null || v === undefined ? '' : String(v));

// A draft row or a submitted value from the server, as screen text.
export function rowFromServer(entry) {
  if (!entry) return EMPTY_ROW;
  return {
    price: entry.no_bid ? '' : trimPrice(entry.price),
    lead: asText(entry.lead_time_days),
    moq: asText(entry.min_order_units),
    no_bid: Boolean(entry.no_bid),
    note: asText(entry.note),
  };
}

export function isEmptyRow(row) {
  return !row.price.trim() && !row.lead.trim() && !row.moq.trim() && !row.no_bid && !row.note.trim();
}

export function sameRow(a, b) {
  return a.price.trim() === b.price.trim() && a.lead.trim() === b.lead.trim()
    && a.moq.trim() === b.moq.trim() && a.no_bid === b.no_bid && a.note.trim() === b.note.trim();
}

// { price?, lead?, moq?, note? } messages. Empty object means the row can be sent.
export function validateRow(row) {
  const errors = {};
  const price = row.price.trim();
  if (price) {
    if (!/^\d+(\.\d+)?$/.test(price)) errors.price = 'Enter the price as a number, like 12.50.';
    else if (/\.\d{5,}$/.test(price)) errors.price = 'Use at most 4 decimals.';
    else if (!(Number(price) > 0)) errors.price = 'The price must be more than 0.';
    else if (Number(price) > PRICE_MAX) errors.price = 'The price is too large.';
    if (row.no_bid) errors.price = 'Enter a price or mark No bid, not both.';
  }
  const lead = row.lead.trim();
  if (lead && (!/^\d+$/.test(lead) || Number(lead) > LEAD_MAX)) {
    errors.lead = `Lead time is whole days, 0 to ${LEAD_MAX.toLocaleString()}.`;
  }
  const moq = row.moq.trim();
  if (moq && (!/^\d+$/.test(moq) || Number(moq) < 1 || Number(moq) > MOQ_MAX)) {
    errors.moq = 'Minimum order is a whole number, 1 or more.';
  }
  if (row.note.length > NOTE_MAX) errors.note = `Keep the note to ${NOTE_MAX.toLocaleString()} characters.`;
  return errors;
}

// A row with details but no price and no No bid is kept in the draft but is
// not part of a submission (the server sends only priced and no-bid parts).
export function rowNeedsAnswer(row) {
  return !isEmptyRow(row) && !row.price.trim() && !row.no_bid;
}

// The row as the draft save expects it. An empty row removes the draft row.
export function rowToPayload(partNumber, row) {
  const price = row.price.trim();
  const lead = row.lead.trim();
  const moq = row.moq.trim();
  const note = row.note.trim();
  return {
    part_number: partNumber,
    price: row.no_bid || !price ? null : price,
    lead_time_days: lead ? Number(lead) : null,
    min_order_units: moq ? Number(moq) : null,
    no_bid: Boolean(row.no_bid),
    note: note || null,
  };
}

// The saved draft row the server now holds for a sent payload (null: removed).
export function draftEntryFromPayload(payload) {
  const empty = payload.price === null && !payload.no_bid && payload.lead_time_days === null
    && payload.min_order_units === null && !payload.note;
  if (empty) return null;
  return {
    price: payload.price, lead_time_days: payload.lead_time_days,
    min_order_units: payload.min_order_units, no_bid: payload.no_bid, note: payload.note,
  };
}

export const SUBMITTED_STATES = ['offer', 'no_bid'];

// Which answer a part has on screen: 'priced', 'no_bid' or 'none'.
export function answerOf(row) {
  if (row.no_bid) return 'no_bid';
  if (row.price.trim()) return 'priced';
  return 'none';
}

// Server reasons for a refused draft row, in plain words.
export const SERVER_ROW_REASONS = {
  not_in_event: 'This part is not in the event.',
  duplicate: 'This part was sent twice.',
};

// Receipt ids are long digests; the first 12 characters are enough to quote.
export function shortReceipt(digest) {
  return digest ? String(digest).slice(0, 12) : '';
}
